import { useCallback, useMemo } from "react";
import { TRANSACTION_TYPE_CATEGORIES } from "helpers/transactionTypeCategories";
import { useSettingsReportValue } from "./useReportStorage";

// Lives in the `settings_report` localStorage object beside the federal tax
// rate, as the selected category values joined by commas -- that object holds
// strings and numbers only.
export const BUCKET3_INCOME_CATEGORIES_KEY = "bucket3_income_categories_strategy";
// Dividends alone: all Bucket 3's Income Received counted before the setting
// existed, and what the backend assumes when none is sent (task 6137).
export const DEFAULT_BUCKET3_INCOME_CATEGORIES = ["dividends"];

// Known values only, in the dropdown's own order whatever order they arrive in.
const inDropdownOrder = (values: Iterable<string>) => {
  const picked = new Set(values);
  return TRANSACTION_TYPE_CATEGORIES.map((category) => category.value).filter(
    (value) => picked.has(value),
  );
};

export const isDefaultBucket3IncomeCategories = (categories: string[]) =>
  categories.join(",") ===
  inDropdownOrder(DEFAULT_BUCKET3_INCOME_CATEGORIES).join(",");

/**
 * The transaction types Bucket 3's Income Received adds up, as Transactions
 * page categories, picked in the Strategy Report's settings and sent with
 * Calculate (task 6137).
 *
 * Returned in the dropdown's own order, whatever order they were ticked in,
 * and never empty: a stored value naming nothing known falls back to the
 * default, since the backend refuses an empty selection.
 */
export const useBucket3IncomeCategories = (): [
  string[],
  (values: string[]) => void,
] => {
  const [storedValue, setStoredValue] = useSettingsReportValue(
    BUCKET3_INCOME_CATEGORIES_KEY,
    DEFAULT_BUCKET3_INCOME_CATEGORIES.join(","),
  );

  const categories = useMemo(() => {
    const known = inDropdownOrder(String(storedValue).split(","));
    return known.length ? known : DEFAULT_BUCKET3_INCOME_CATEGORIES;
  }, [storedValue]);

  const setCategories = useCallback(
    (values: string[]) => {
      const known = inDropdownOrder(values);
      if (known.length) setStoredValue(known.join(","));
    },
    [setStoredValue],
  );

  return [categories, setCategories];
};
